import { buildTask as _buildTask } from "ember-concurrency/-private/async-arrow-runtime";
var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(target, property, descriptor, context) { if (!descriptor) return; Object.defineProperty(target, property, { enumerable: descriptor.enumerable, configurable: descriptor.configurable, writable: descriptor.writable, value: descriptor.initializer ? descriptor.initializer.call(context) : void 0 }); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
function _initializerWarningHelper(descriptor, context) { throw new Error('Decorating class property failed. Please ensure that ' + 'proposal-class-properties is enabled and runs after the decorators transform.'); }
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { format, getDate, isBefore, isToday, startOfDay, addMinutes } from 'date-fns';
import { getPartialTimes } from 'garaje/utils/hour-options';
import { APPROVAL_STATUS, INVITE_STATE } from 'garaje/utils/enums';
import { task } from 'ember-concurrency';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import config from 'garaje/config/environment';
import { get } from '@ember/object';

/**
 * @param {Array}             reservations      array of gql reservations
 * @param                      screeningCard     screeningCard for the day
 * @param {Date}              date              date for the invite
 * @param {number}            requiredDocumentApprovalExpiresAt
 * @param {boolean}           hasRequiredDocumentApproval
 * @param {Function}          selectInvite
 * @param {boolean}           bookDeskEnabled
 * @param                    invite            graphql representation of an invite
 * @param {number}            maxOccupancy      amount allowed in the office
 * @param {number}            peopleRegistered  total amount of people with invites for the day (not desk reservations)
 */
let DayAvailabilityComponent = (_class = class DayAvailabilityComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "state", _descriptor, this);
    _initializerDefineProperty(this, "metrics", _descriptor2, this);
    _initializerDefineProperty(this, "flashMessages", _descriptor3, this);
    _initializerDefineProperty(this, "registrations", _descriptor4, this);
    _defineProperty(this, "INVITE_STATE_ENUM", INVITE_STATE);
    _defineProperty(this, "signInInviteTask", _buildTask(() => ({
      context: this,
      generator: function* () {
        const {
          screeningCard,
          reservation,
          date
        } = this.args;
        try {
          const entry = yield this.registrations.signInInvitePartialDay(date);
          this.metrics.trackEvent('Web Employee Schedule - Sign In', {
            desk_id: reservation?.desk.id,
            entry_flow_name: entry.flowName,
            entry_id: entry.id,
            entry_location_id: entry.location.id,
            invite_date: screeningCard.expectedArrivalTime,
            invite_id: screeningCard.id,
            reservation_id: reservation?.id
          });
          this.flashMessages.showAndHideFlash('success', 'Signed in');
        } catch (e) {
          console.error('error', e); // eslint-disable-line no-console
          this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e.errors?.firstObject));
        }
      }
    }), {
      drop: true
    }, "signInInviteTask", null));
    _defineProperty(this, "signOutEntryTask", _buildTask(() => ({
      context: this,
      generator: function* () {
        const {
          date,
          reservation,
          screeningCard
        } = this.args;
        try {
          const entry = yield this.registrations.signOutEntryPartialDay(date);
          this.metrics.trackEvent('Web Employee Schedule - Sign Out', {
            desk_id: reservation?.desk.id,
            employee_entry_sign_out_at: entry.signedOutAt,
            entry_date: entry.signed,
            entry_flow_name: entry.flowName,
            entry_id: entry.id,
            entry_location_id: entry.location.id,
            invite_id: screeningCard.id,
            reservation_id: reservation?.id
          });
          this.flashMessages.showAndHideFlash('success', 'Signed out');
        } catch (e) {
          console.error('error', e); // eslint-disable-line no-console
          this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e.errors?.firstObject));
        }
      }
    }), {
      drop: true
    }, "signOutEntryTask", null));
  }
  get dayOfWeek() {
    return format(this.args.date, 'eee');
  }
  get date() {
    return getDate(this.args.date);
  }
  get darkMode() {
    return [INVITE_STATE.SCHEDULED, INVITE_STATE.APPROVED, INVITE_STATE.PENDING, INVITE_STATE.SIGNED_IN].includes(this.inviteState);
  }
  get errorMode() {
    return [INVITE_STATE.DENIED, INVITE_STATE.PAST_WINDOW].includes(this.inviteState);
  }
  get isNotRegistered() {
    return !this.isApproved && !this.isDenied;
  }
  get invite() {
    return this.args.screeningCard?.__typename == 'Invite' ? this.args.screeningCard : null;
  }
  get approvalStatus() {
    return this.invite?.approvalStatus?.status;
  }
  get isApproved() {
    return this.approvalStatus == APPROVAL_STATUS.APPROVED;
  }
  get isDenied() {
    return this.approvalStatus == APPROVAL_STATUS.DENIED;
  }
  get isToday() {
    return isToday(this.args.date);
  }
  get preregistrationLink() {
    return this.invite?.preregistrationLink || '';
  }
  get registerUrl() {
    const garajeReturnUrl = `${config.host}/schedule%3FselectedInviteId%3D${this.invite?.id}`;
    return this.noRequiredDocumentApproval ? null : `${this.preregistrationLink}?garajeReturnUrl=${garajeReturnUrl}`;
  }
  get screeningWindowStarted() {
    const {
      registrationEligibilityStartOffset,
      employeeScreeningFlow
    } = this.state.currentLocation;
    if (employeeScreeningFlow && !get(employeeScreeningFlow, 'employeeScreeningRequired')) {
      return true;
    }
    const startOfWindow = addMinutes(startOfDay(this.args.date), registrationEligibilityStartOffset || 0);
    return !isBefore(new Date(), startOfWindow);
  }
  get screeningWindowEnded() {
    const {
      registrationEligibilityEndOffset,
      employeeScreeningFlow
    } = this.state.currentLocation;
    if (employeeScreeningFlow && !get(employeeScreeningFlow, 'employeeScreeningRequired')) {
      return false;
    }
    const endOfWindow = addMinutes(startOfDay(this.args.date), registrationEligibilityEndOffset || 0);
    return !isBefore(new Date(), endOfWindow);
  }
  get isShowingInviteCta() {
    return this.screeningWindowStarted && [INVITE_STATE.SCHEDULED, INVITE_STATE.APPROVED, INVITE_STATE.SIGNED_IN].includes(this.inviteState);
  }
  get inviteState() {
    if (this.isNotRegistered) {
      if (this.screeningWindowEnded) {
        return INVITE_STATE.PAST_WINDOW;
      } else if (!this.invite) {
        return INVITE_STATE.UNSCHEDULED;
      }
      return INVITE_STATE.SCHEDULED;
    } else {
      if (!this.invite?.entry) {
        // registered states
        if (this.isApproved) return INVITE_STATE.APPROVED;
        if (this.isDenied) return INVITE_STATE.DENIED;
        return INVITE_STATE.PENDING;
      }
      return INVITE_STATE.SIGNED_IN;
    }
  }
  get noRequiredDocumentApproval() {
    const {
      date,
      requiredDocumentApprovalExpiresAt,
      hasRequiredDocumentApproval
    } = this.args;
    const isExpiredDocument = requiredDocumentApprovalExpiresAt && !isBefore(date, new Date(requiredDocumentApprovalExpiresAt));
    return isExpiredDocument || !hasRequiredDocumentApproval;
  }
  get isDayBookable() {
    const {
      date,
      workplaceDays
    } = this.args;
    if (workplaceDays) {
      const currentDay = format(date, 'eeee');
      const workplaceDay = workplaceDays.find(day => day.dayOfWeek === currentDay);
      return workplaceDay.active;
    }
    return true;
  }
  get bookDeskEnabled() {
    const {
      bookDeskEnabled,
      date,
      workplaceDays
    } = this.args;
    const currentDay = format(date, 'eeee');
    const workplaceDay = workplaceDays.find(day => day.dayOfWeek === currentDay);
    const [_, partialEnd] = getPartialTimes(startOfDay(date), workplaceDay);
    if (isToday(date)) {
      return bookDeskEnabled && isBefore(new Date(), partialEnd);
    }
    return bookDeskEnabled;
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "state", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "metrics", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "flashMessages", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "registrations", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
})), _class);
export { DayAvailabilityComponent as default };